import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import './AdminClients.css';
import {
  UserCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import {
  listAdminUnreadConversations,
  markConversationMessagesRead,
} from '../../api/backend';
import {
  deleteClientRecord,
  fetchAdminClientsSnapshot,
  sendClientAccessLink,
  setClientStatus,
  upsertClientRecord,
} from '../../services/adminClientsApi';
import { sendLeadEmail } from '../../services/messagingApi';
import { linkHostingPackage, unlinkHostingPackage } from '../../services/hostingApi';
import { db } from '../../firebase';

const MONTH_LABELS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// El admin elige día (+mes si es anual), no una fecha de calendario completa
// — más fácil de leer y de confirmar mirando una factura vieja ("vence cada
// 15 de noviembre") que picar una fecha exacta con año. Esto calcula la
// próxima ocurrencia real (este año/mes si todavía no pasó, si no el
// siguiente) para mandarla al backend, que sigue esperando una fecha normal.
function nextOccurrenceIso({ day, month, frequency }) {
  const d = Number(day);
  if (!d) return '';
  const now = new Date();
  if (frequency === 'monthly') {
    const candidate = new Date(now.getFullYear(), now.getMonth(), d);
    if (candidate <= now) candidate.setMonth(candidate.getMonth() + 1);
    return candidate.toISOString().slice(0, 10);
  }
  const m = Number(month);
  if (!m) return '';
  const candidate = new Date(now.getFullYear(), m - 1, d);
  if (candidate <= now) candidate.setFullYear(candidate.getFullYear() + 1);
  return candidate.toISOString().slice(0, 10);
}

const API_BASE = 'https://us-central1-tarjeta-ad945.cloudfunctions.net';

const PRODUCT_LINE_LABELS = {
  erp: 'CRM/ERP (Book Me / Clinic 360)',
  restaurante: 'Restaurantes (Meso 360)',
};

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newClient, setNewClient] = useState({
    businessName: '',
    phone: '',
    email: '',
    address: '',
  });
  const [editingClientId, setEditingClientId] = useState(null);
  const [editData, setEditData] = useState({});
  const [hostingPackages, setHostingPackages] = useState([]);
  const [linkingDomain, setLinkingDomain] = useState(false);
  const navigate = useNavigate();
  const [newClientMessages, setNewClientMessages] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [productLineFilter, setProductLineFilter] = useState('all');
  const [messagesClient, setMessagesClient] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [replyChannel, setReplyChannel] = useState('email');
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState('');

  const openMessagesModal = async (client) => {
    setMessagesClient(client);
    setReplyChannel('email');
    setReplySubject(`Re: ${client.businessName || client.name || client.email || 'tu consulta'}`);
    setReplyMessage('');
    setReplyError('');
    setConversationLoading(true);
    try {
      const convRef = collection(db, 'messaging_conversations', `email_${client.id}`, 'messages');
      const snap = await getDocs(query(convRef, orderBy('createdAt', 'desc'), limit(5)));
      setConversationMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse());
    } catch (error) {
      console.error('Error al cargar la conversación:', error);
      setConversationMessages([]);
    } finally {
      setConversationLoading(false);
    }
  };

  const closeMessagesModal = () => {
    setMessagesClient(null);
    setConversationMessages([]);
    setReplyMessage('');
    setReplySubject('');
    setReplyError('');
  };

  const handleReplySend = async () => {
    if (!messagesClient || !replyMessage.trim()) return;
    setReplySending(true);
    setReplyError('');
    try {
      if (replyChannel === 'email') {
        if (!messagesClient.email) throw new Error('Este cliente no tiene correo guardado.');
        await sendLeadEmail({
          profileId: messagesClient.id,
          subject: replySubject.trim() || 'Mensaje de BBTS',
          message: replyMessage.trim(),
        });
      } else {
        const phone = messagesClient.phone?.trim();
        if (!phone) throw new Error('Este cliente no tiene teléfono guardado.');
        const res = await fetch(`${API_BASE}/sendWhatsAppReply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, message: replyMessage.trim() }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Error al enviar por WhatsApp');
        }
      }
      setReplyMessage('');
      await openMessagesModal(messagesClient);
    } catch (error) {
      console.error('Error al responder:', error);
      setReplyError(error.message || 'No se pudo enviar el mensaje.');
    } finally {
      setReplySending(false);
    }
  };

  const sortClients = (items = []) => (
    [...items].sort((a, b) =>
      String(a.businessName || a.name || '').localeCompare(String(b.businessName || b.name || ''), 'es')
    )
  );

  const upsertClient = (client) => {
    if (!client?.id) return;

    setClients((prev) => {
      const next = prev.filter((item) => item.id !== client.id);
      return sortClients([...next, client]);
    });
  };

  const removeClient = (clientId) => {
    setClients((prev) => prev.filter((item) => item.id !== clientId));
  };

  const fetchClients = async () => {
    try {
      const response = await fetchAdminClientsSnapshot();
      const clientList = response.clients || [];
      setClients(clientList);
      setFilteredClients(clientList);
      return clientList;
    } catch (error) {
      console.error('Error al obtener clientes:', error);
      throw error;
    }
  };
  
  useEffect(() => {
    let cancelled = false;

    const fetchUnreadMessages = async () => {
      try {
        const conversations = await listAdminUnreadConversations();
        if (cancelled) return;
        setNewClientMessages(
          conversations.map((item) => ({
            clientId: item.clientId,
            clientEmail: item.clientEmail,
            body: item.latestMessage?.body || '',
          }))
        );
        setShowPopup(conversations.length > 0);
      } catch (error) {
        console.error('Error al cargar mensajes no leídos:', error);
      }
    };

    fetchUnreadMessages();
    const intervalId = window.setInterval(fetchUnreadMessages, 10000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);
  
  

  useEffect(() => {
    fetchClients().catch((error) => {
      console.error('Error inicial al cargar clientes:', error);
    });
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'hosting_packages'), orderBy('name', 'asc'));
    const unsub = onSnapshot(q, (snap) => setHostingPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, []);

  useEffect(() => {
    const filtered = clients
      .filter((c) => (statusFilter === 'inactive' ? c.status === 'inactive' : c.status !== 'inactive'))
      .filter((c) => {
        if (productLineFilter === 'all') return true;
        if (productLineFilter === 'unset') return !c.productLine;
        return c.productLine === productLineFilter;
      })
      .filter((c) =>
        c.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.includes(searchTerm)
      );
    setFilteredClients(filtered);
  }, [searchTerm, clients, statusFilter, productLineFilter]);

  const handleToggleStatus = async (client) => {
    const nextStatus = client.status === 'inactive' ? 'active' : 'inactive';
    try {
      await setClientStatus({ clientId: client.id, status: nextStatus });
      upsertClient({ ...client, status: nextStatus });
    } catch (error) {
      console.error('Error al cambiar estado del cliente:', error);
      alert('No se pudo cambiar el estado del cliente.');
    }
  };

  const exportClientsForMarketing = () => {
    const rows = filteredClients.filter((c) => c.email);
    if (!rows.length) { alert('No hay clientes con email en este segmento para exportar.'); return; }
    const header = 'name,businessName,email,phone,productLine\n';
    const body = rows.map((c) => [
      c.businessName || '', c.businessName || '', c.email || '', c.phone || '', c.productLine || '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clientes-${statusFilter}-${productLineFilter}-para-mercadeo.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleDeleteClient = async (client) => {
    const confirm = window.confirm(`¿Estás seguro que deseas eliminar al cliente ${client.businessName}? Esta acción no se puede deshacer.`);
    if (!confirm) return;
  
    try {
      await deleteClientRecord({ clientId: client.id });
      removeClient(client.id);
      alert(' Cliente eliminado correctamente');
      try {
        await fetchClients();
      } catch (refreshError) {
        console.warn('La recarga de clientes falló después de eliminar; se mantiene la lista local.', refreshError);
      }
    } catch (error) {
      console.error(' Error al eliminar cliente:', error);
      alert('Error al eliminar cliente');
    }
  };
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const response = await upsertClientRecord({
        payload: {
          ...newClient,
          email: newClient.email.trim().toLowerCase(),
        },
        sendAccessLink: true,
      });
      const savedClient = response.client;

      upsertClient(savedClient);

      alert('Cliente creado con éxito. Se ha enviado un correo para establecer contraseña.');

      setNewClient({ businessName: '', phone: '', email: '', address: '' });
      setShowNewClientModal(false);
      try {
        await fetchClients();
      } catch (refreshError) {
        console.warn('La recarga de clientes falló después de crear; se mantiene la lista local.', refreshError);
      }
    } catch (error) {
      console.error('Error al crear cliente:', error);
  
      // ⚠️ Mejora: manejar errores específicos
      if (error.code === 'auth/email-already-in-use') {
        alert('El correo ya está registrado. Usa otro.');
      } else {
        alert('Error al crear cliente. Ver consola.');
      }
    }
  };
  
  const handleSendResetLink = async (email) => {
    if (!email) return alert('Este cliente no tiene correo.');
  
    const confirmed = window.confirm(`¿Enviar correo de acceso a ${email}?`);
    if (!confirmed) return;
  
    try {
      await sendClientAccessLink({
        email,
        clientName: email,
      });
      alert('✅ Enlace de acceso enviado con éxito.');
    } catch (err) {
      console.error('❌ Error al contactar backend:', err);
      alert('No se pudo enviar el correo. Ver consola.');
    }
  };
  

  const startEdit = (client) => {
    setEditingClientId(client.id);
    const renewalDate = client.hostingRenewalDate?.toDate ? client.hostingRenewalDate.toDate() : (client.hostingRenewalDate ? new Date(client.hostingRenewalDate) : null);
    setEditData({
      businessName: client.businessName,
      phone: client.phone,
      email: client.email,
      address: client.address,
      productLine: client.productLine || '',
      renewalDay: renewalDate ? String(renewalDate.getDate()) : '',
      renewalMonth: renewalDate ? String(renewalDate.getMonth() + 1) : '',
      billingFrequency: client.hostingBillingFrequency || 'annual',
      hostingPackageId: client.hostingPackageId || '',
      hostingAmount: client.hostingAmount ? String(client.hostingAmount) : '',
    });
  };

  const saveEdit = async (id) => {
    try {
      const { renewalDay, renewalMonth, hostingPackageId, ...rest } = editData;
      const renewalDate = renewalDay
        ? nextOccurrenceIso({ day: renewalDay, month: renewalMonth, frequency: editData.billingFrequency })
        : '';
      const response = await upsertClientRecord({
        clientId: id,
        payload: {
          ...rest,
          renewalDate,
          email: String(editData.email || '').trim().toLowerCase(),
        },
        sendAccessLink: false,
      });
      const savedClient = response.client;
      upsertClient(savedClient);
      alert(response.emailConflict ? `Cliente actualizado. ${response.emailConflictMessage}` : 'Cliente actualizado');
      setEditingClientId(null);
      try {
        await fetchClients();
      } catch (refreshError) {
        console.warn('La recarga de clientes falló después de editar; se mantiene la lista local.', refreshError);
      }
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
    }
  };

  const linkDomainToEditingClient = async (packageId) => {
    if (!packageId || !editingClientId) return;
    setLinkingDomain(true);
    try {
      const renewalDate = editData.renewalDay
        ? nextOccurrenceIso({ day: editData.renewalDay, month: editData.renewalMonth, frequency: editData.billingFrequency })
        : '';
      await linkHostingPackage({ packageId, clientId: editingClientId, renewalDate, billingFrequency: editData.billingFrequency || 'annual', amount: editData.hostingAmount || undefined });
      setEditData((prev) => ({ ...prev, hostingPackageId: packageId }));
      await fetchClients();
    } catch (error) {
      alert(error.message || 'No se pudo vincular el dominio.');
    } finally {
      setLinkingDomain(false);
    }
  };

  const unlinkDomainFromEditingClient = async () => {
    if (!editData.hostingPackageId) return;
    setLinkingDomain(true);
    try {
      await unlinkHostingPackage({ packageId: editData.hostingPackageId });
      setEditData((prev) => ({ ...prev, hostingPackageId: '' }));
      await fetchClients();
    } catch (error) {
      alert(error.message || 'No se pudo desvincular el dominio.');
    } finally {
      setLinkingDomain(false);
    }
  };

  const handleViewInvoices = (client) => {
    const clientUid = client.uid || client.id; // usa UID si existe
    navigate(`/admin/clients/${clientUid}/invoices`);
  };
  
  

  if (!clients) {
    return <p>Cargando clientes...</p>;
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold">Gestión de Clientes</h2>
          <button
            className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
            onClick={() => setShowNewClientModal(true)}
          >
            ➕ Nuevo Cliente
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex rounded-lg border overflow-hidden">
            <button
              type="button"
              onClick={() => setStatusFilter('active')}
              className={`px-4 py-2 text-sm font-semibold ${statusFilter === 'active' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}
            >
              Activos
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('inactive')}
              className={`px-4 py-2 text-sm font-semibold border-l ${statusFilter === 'inactive' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}
            >
              Inactivos
            </button>
          </div>
          <select
            value={productLineFilter}
            onChange={(e) => setProductLineFilter(e.target.value)}
            className="px-3 py-2 text-sm border rounded-lg bg-white text-gray-700"
            title="Segmentar por línea de producto para saber a quién le llegan las promos"
          >
            <option value="all">Todas las líneas</option>
            <option value="erp">{PRODUCT_LINE_LABELS.erp}</option>
            <option value="restaurante">{PRODUCT_LINE_LABELS.restaurante}</option>
            <option value="unset">Sin clasificar</option>
          </select>

          <button
            type="button"
            onClick={exportClientsForMarketing}
            className="text-sm px-3 py-2 rounded border border-sky-300 text-sky-700 hover:bg-sky-50"
            title="Descarga un CSV del segmento actual, listo para subir en Mercadeo → Subir lista (CSV)"
          >
            Exportar CSV para Mercadeo
          </button>
        </div>

        <input
          type="text"
          placeholder="Buscar por nombre o teléfono"
          className="w-full mb-6 p-3 border rounded"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {showPopup && newClientMessages.length > 0 && (
          <div className="mb-4 bg-yellow-100 border border-yellow-400 text-yellow-800 p-4 rounded">
            <strong>📬 Mensajes no leídos:</strong>
            <ul className="mt-2">
              {newClientMessages.map((msg, idx) => (
                <li key={idx} className="flex justify-between items-center mb-1">
                  <span>{msg.clientEmail}: {msg.body.slice(0, 40)}...</span>
                  <button
                    onClick={async () => {
                      const targetId = msg.clientId || msg.clientEmail.trim().toLowerCase();
                      try {
                        await markConversationMessagesRead({ clientId: targetId });
                      } catch (error) {
                        console.error('Error marcando mensajes como leídos:', error);
                      }
                      navigate(`/admin/clients/${encodeURIComponent(targetId)}/email`);
                      setShowPopup(false);
                    }}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Ver chat
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Lista de clientes */}
        <div className="grid gap-3">
          {filteredClients.length === 0 ? (
            <p className="rounded-3xl border border-gray-200 bg-white px-5 py-8 text-center text-sm text-gray-400">No hay clientes encontrados.</p>
          ) : (
            filteredClients.map((client) => (
              <div key={client.id} className="rounded-3xl border border-gray-200 bg-white px-5 py-4 transition hover:border-gray-300 hover:bg-gray-50/60">
                {editingClientId === client.id ? (
                  <>
                    <input
                      className="w-full mb-2 p-2 border rounded"
                      value={editData.businessName}
                      name="businessName"
                      onChange={(e) => setEditData({ ...editData, businessName: e.target.value })}
                    />
                    <input
                      className="w-full mb-2 p-2 border rounded"
                      value={editData.phone}
                      name="phone"
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    />
                    <input
                      className="w-full mb-2 p-2 border rounded"
                      value={editData.email}
                      name="email"
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    />
                    <input
                      className="w-full mb-2 p-2 border rounded"
                      value={editData.address}
                      name="address"
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    />
                    <select
                      className="w-full mb-2 p-2 border rounded"
                      value={editData.productLine || ''}
                      name="productLine"
                      onChange={(e) => setEditData({ ...editData, productLine: e.target.value })}
                    >
                      <option value="">Sin clasificar</option>
                      <option value="erp">{PRODUCT_LINE_LABELS.erp}</option>
                      <option value="restaurante">{PRODUCT_LINE_LABELS.restaurante}</option>
                    </select>
                    <label className="mb-1 mt-2 block text-xs font-semibold text-gray-500">Dominio de hosting (20i)</label>
                    {editData.hostingPackageId ? (
                      <div className="mb-2 flex items-center justify-between gap-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-sm">
                        <span className="text-emerald-800">
                          {hostingPackages.find((p) => p.id === editData.hostingPackageId)?.domains?.[0] || 'Vinculado'}
                        </span>
                        <button type="button" onClick={unlinkDomainFromEditingClient} disabled={linkingDomain} className="text-xs font-semibold text-rose-600 hover:underline">Quitar</button>
                      </div>
                    ) : (
                      <select
                        className="w-full mb-2 p-2 border rounded text-sm"
                        value=""
                        disabled={linkingDomain}
                        onChange={(e) => linkDomainToEditingClient(e.target.value)}
                      >
                        <option value="">Sin dominio — selecciona para vincular…</option>
                        {hostingPackages.filter((p) => !p.linkedClientId).map((p) => (
                          <option key={p.id} value={p.id}>{p.domains?.[0] || p.name}</option>
                        ))}
                      </select>
                    )}

                    <label className="mb-1 mt-2 block text-xs font-semibold text-gray-500">
                      Vence cada {editData.billingFrequency === 'monthly' ? 'mes, el día' : 'año, el'} {!editData.hostingPackageId && '(sin dominio — licencia/mensualidad con BBTS)'}
                    </label>
                    <div className="mb-2 flex gap-2">
                      <select
                        className="w-20 p-2 border rounded text-sm"
                        value={editData.renewalDay || ''}
                        onChange={(e) => setEditData({ ...editData, renewalDay: e.target.value })}
                      >
                        <option value="">Día</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                      {editData.billingFrequency !== 'monthly' && (
                        <select
                          className="flex-1 p-2 border rounded text-sm"
                          value={editData.renewalMonth || ''}
                          onChange={(e) => setEditData({ ...editData, renewalMonth: e.target.value })}
                        >
                          <option value="">Mes</option>
                          {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                      )}
                      <select
                        className="p-2 border rounded text-sm"
                        value={editData.billingFrequency || 'annual'}
                        onChange={(e) => setEditData({ ...editData, billingFrequency: e.target.value })}
                      >
                        <option value="annual">Anual</option>
                        <option value="monthly">Mensual</option>
                      </select>
                    </div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Monto a facturar cada {editData.billingFrequency === 'monthly' ? 'mes' : 'año'} (opcional — si lo pones, se genera la factura sola cuando se acerque la renovación)
                    </label>
                    <div className="mb-2 flex items-center gap-1">
                      <span className="text-sm text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-28 p-2 border rounded text-sm"
                        value={editData.hostingAmount || ''}
                        onChange={(e) => setEditData({ ...editData, hostingAmount: e.target.value })}
                      />
                    </div>
                    <div className="flex justify-between">
                      <button
                        onClick={() => saveEdit(client.id)}
                        className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                      >
                        Guardar
                      </button>
                      <button
                        onClick={() => setEditingClientId(null)}
                        className="bg-gray-300 text-black px-3 py-1 rounded"
                      >
                        Cancelar
                      </button>
                    </div>
                  </>
         ) : (
          <>
            {(() => {
              const unread = newClientMessages.some((m) => (m.clientId || m.clientEmail?.trim().toLowerCase()) === (client.id || client.email));
              const renewalDate = client.hostingRenewalDate ? (client.hostingRenewalDate.toDate ? client.hostingRenewalDate.toDate() : new Date(client.hostingRenewalDate)) : null;
              const renewalDaysLeft = renewalDate ? Math.ceil((renewalDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)) : null;
              const renewalOverdue = renewalDaysLeft !== null && renewalDaysLeft < 0;
              const renewalSoon = renewalDaysLeft !== null && renewalDaysLeft >= 0 && renewalDaysLeft <= 14;
              return (
                <div className="grid gap-4 lg:grid-cols-[minmax(0,2.1fr)_1.1fr_auto] lg:items-center">
                  {/* Columna 1: identidad + badges de estado */}
                  <div className="min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <img
                        src={client.profileImageUrl || '/default-avatar.png'}
                        alt={client.businessName || client.email}
                        className="h-9 w-9 flex-shrink-0 rounded-full border border-gray-200 object-cover"
                      />
                      <h3 className="truncate text-base font-semibold text-gray-900">{client.businessName || 'Cliente sin nombre'}</h3>
                      {unread && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700">
                          📬 Sin leer
                        </span>
                      )}
                      {renewalOverdue && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-semibold text-red-700">
                          ⏰ Renovación vencida
                        </span>
                      )}
                      {renewalSoon && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">
                          Renueva en {renewalDaysLeft}d
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 pl-12 text-sm text-gray-500">
                      {client.email && <span>{client.email}</span>}
                      {client.phone && <span>{client.phone}</span>}
                      {client.address && <span>{client.address}</span>}
                    </div>
                  </div>

                  {/* Columna 2: línea de producto + renovación */}
                  <div className="flex flex-wrap gap-1.5 lg:justify-start">
                    {client.productLine ? (
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${client.productLine === 'restaurante' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
                        {PRODUCT_LINE_LABELS[client.productLine] || client.productLine}
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-500">Sin clasificar</span>
                    )}
                    {renewalDate && (
                      <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold text-sky-700">
                        Renueva: {renewalDate.toLocaleDateString('es-PR')} ({client.hostingBillingFrequency === 'monthly' ? 'mensual' : 'anual'})
                      </span>
                    )}
                  </div>

                  {/* Columna 3: acciones */}
                  <div className="flex flex-wrap gap-1.5 lg:justify-end">
                    <button onClick={() => startEdit(client)} className="rounded-2xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50">Editar</button>
                    <button onClick={() => handleSendResetLink(client.email)} className="rounded-2xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">Credenciales</button>
                    <button onClick={() => handleViewInvoices(client)} className="rounded-2xl bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600">Administrar</button>
                    <button onClick={() => openMessagesModal(client)} className="rounded-2xl bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-600">Mensajes</button>
                    <button onClick={() => handleToggleStatus(client)} className="rounded-2xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                      {client.status === 'inactive' ? 'Reactivar' : 'Marcar inactivo'}
                    </button>
                    <button onClick={() => handleDeleteClient(client)} className="rounded-2xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">Eliminar</button>
                  </div>
                </div>
              );
            })()}
          </>
                )}
              </div>
            ))
          )}
        </div>

{/* Modal Nuevo Cliente */}
{showNewClientModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-md">
      <h3 className="text-xl font-bold mb-4 text-gray-800">Nuevo Cliente</h3>
      <form onSubmit={handleCreateClient} className="space-y-4">
        {/* Nombre */}
        <div className="relative">
          <UserCircleIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
          <input
            name="businessName"
            required
            placeholder="Nombre del Negocio"
            className="w-full pl-10 p-2 border rounded"
            value={newClient.businessName}
            onChange={(e) =>
              setNewClient({ ...newClient, businessName: e.target.value })
            }
          />
        </div>

        {/* Teléfono */}
        <div className="relative">
          <PhoneIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
          <input
            name="phone"
            required
            placeholder="Teléfono"
            className="w-full pl-10 p-2 border rounded"
            value={newClient.phone}
            onChange={(e) =>
              setNewClient({ ...newClient, phone: e.target.value })
            }
          />
        </div>

        {/* Email */}
        <div className="relative">
          <EnvelopeIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
          <input
            name="email"
            required
            placeholder="Correo Electrónico"
            className="w-full pl-10 p-2 border rounded"
            value={newClient.email}
            onChange={(e) =>
              setNewClient({ ...newClient, email: e.target.value })
            }
          />
        </div>

        {/* Dirección */}
        <div className="relative">
          <MapPinIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
          <input
            name="address"
            required
            placeholder="Dirección"
            className="w-full pl-10 p-2 border rounded"
            value={newClient.address}
            onChange={(e) =>
              setNewClient({ ...newClient, address: e.target.value })
            }
          />
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={() => setShowNewClientModal(false)}
            className="px-4 py-2 bg-gray-300 rounded"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            Crear
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{messagesClient && (
  <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center px-4">
    <div className="bg-white w-full max-w-lg rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center border-b px-6 py-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Mensajes con {messagesClient.businessName || messagesClient.email}
        </h3>
        <button onClick={closeMessagesModal} className="text-gray-500 hover:text-red-600">✕</button>
      </div>

      <div className="px-6 py-4 space-y-3">
        {conversationLoading ? (
          <p className="text-xs text-gray-400">Cargando conversación…</p>
        ) : conversationMessages.length > 0 ? (
          <div className="space-y-1.5 max-h-28 overflow-y-auto rounded border border-sky-100 bg-sky-50/50 p-2">
            {conversationMessages.map((msg) => (
              <p key={msg.id} className="text-xs text-gray-600">
                <span className={`font-semibold ${msg.direction === 'inbound' ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {msg.direction === 'inbound' ? 'Cliente: ' : 'Tú: '}
                </span>
                {msg.textBody?.slice(0, 140) || '(sin texto)'}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Sin correos previos con este cliente todavía.</p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setReplyChannel('email')}
            className={`px-3 py-1 text-xs rounded-full font-medium ${replyChannel === 'email' ? 'bg-sky-600 text-white' : 'bg-white text-sky-700 border border-sky-200'}`}
          >
            Por correo
          </button>
          <button
            type="button"
            onClick={() => setReplyChannel('whatsapp')}
            disabled={!messagesClient.phone}
            className={`px-3 py-1 text-xs rounded-full font-medium disabled:opacity-40 ${replyChannel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 border border-emerald-200'}`}
            title={!messagesClient.phone ? 'Este cliente no tiene teléfono guardado' : ''}
          >
            Por WhatsApp
          </button>
        </div>

        {replyChannel === 'email' && (
          <input
            type="text"
            value={replySubject}
            onChange={(e) => setReplySubject(e.target.value)}
            placeholder="Asunto"
            className="w-full rounded border px-3 py-1.5 text-sm"
          />
        )}
        <textarea
          value={replyMessage}
          onChange={(e) => setReplyMessage(e.target.value)}
          placeholder={replyChannel === 'email' ? 'Escribe tu respuesta…' : 'Escribe tu mensaje de WhatsApp…'}
          rows={3}
          className="w-full rounded border px-3 py-1.5 text-sm"
        />
        {replyError && <p className="text-xs text-red-600">{replyError}</p>}
        <button
          type="button"
          onClick={handleReplySend}
          disabled={replySending || !replyMessage.trim()}
          className="rounded bg-sky-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
        >
          {replySending ? 'Enviando…' : 'Enviar'}
        </button>
      </div>
    </div>
  </div>
)}

      </div>
    </div>
  );
};

export default AdminClients;
